import { Component, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ItineraryService } from '../../services/itinerary.service';
import { PnrAvailableOption, PnrInfo, SearchForm } from 'src/app/core/models/itinerary.model';
import { PassengerQuote, QuoteRequest } from 'src/app/core/models/itinerary.model';
import { LOCALSTORAGEVARS, OptionType, PAGETYPE } from 'src/app/core/enums/enums';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import { IntegrationsService } from 'src/app/integrations/services/integrations.service';
import { ServicesIntegration } from 'src/app/integrations/models/services-integration.enum';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { take } from 'rxjs/operators';
import { INoShowRegulationSetting } from 'src/app/core/models/agency-settings.model';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { Router } from '@angular/router';
import { ReissueService } from 'src/app/reissue/services/reissue.service';

@Component({
  selector: 'app-search-result',
  templateUrl: './search-result.component.html',
  styleUrls: ['./search-result.component.scss']
})
export class SearchResultComponent implements OnInit {
  activeTab$: Observable<string>;
  loadingActions$: Observable<boolean>;
  action$: Observable<PnrAvailableOption>;
  selectedPassengersQuote$: Observable<PassengerQuote[]>;
  exceptionMode$: Observable<boolean>;
  selectedSegments$: Observable<number[]>;
  somePassengerHasTicket$: Observable<boolean>;
  pnrInfo: PnrInfo;
  searchForm$: Observable<SearchForm>;
  currentProcess$: Observable<ServicesIntegration>;
  noShowRegulationEnabled: boolean = false;
  showAvailableOptionModal: boolean;

  constructor(
    private service: ItineraryService,
    private layoutService: LayoutService,
    private vcService: VoluntaryChangeService,
    private integService: IntegrationsService,
    private settingsService: SettingsService,
    private reissueService: ReissueService,
    private router: Router
  ) {}

  ngOnInit() {
    this.resetFlowVariables();
    this.vcService.resetNotExchangeAllowed();
    this.activeTab$ = this.service.getActiveTab();
    this.service.dispatchExceptionMode(false);
    this.service.dispatchHideOrShowExceptionSwitch(true);
    this.action$ = this.service.getActions();
    this.searchForm$ = this.service.getSearchForm();
    this.exceptionMode$ = this.layoutService.getShowException();
    this.selectedSegments$ = this.service.getSelectedSegments();
    this.selectedPassengersQuote$ = this.service.getSelectedPassengersQuote();
    this.somePassengerHasTicket$ = this.service.getSomePassengerHasTicket();
    this.currentProcess$ = this.integService.selectCurrentProcess();
    this.service
      .getPnrInfo()
      .pipe(take(1))
      .subscribe((info) => {
        this.pnrInfo = info;
        this.settingsService.getClientSettings().subscribe((settings) => {
          this.reissueService.clearReissue();

          if (settings.isAirline && settings.gdsOversignEnabled) {
            this.layoutService.getBookingCredentialsState().subscribe((state) => {
              if (state.initialRequirement) {
                this.layoutService.requireBookingCredentials();
              }
            });
          }
          if (settings.noShowRegulation && settings.noShowRegulation.length > 0) {
            settings.noShowRegulation.forEach((regulation) => {
              this.validateNoShowRegulationEnabled(regulation, info);
            });
          }
        });
      });
  }

  resetFlowVariables() {
    if (this.router.url.includes(PAGETYPE.itinerary)) {
      localStorage.removeItem(LOCALSTORAGEVARS.redirectFromManual);
      localStorage.removeItem(LOCALSTORAGEVARS.redirectFromAutomatic);
    }
  }

  validateNoShowRegulationEnabled(regulation: INoShowRegulationSetting, pnrInfo: PnrInfo) {
    if (regulation && regulation?.noShowValidation && pnrInfo.noShowRegulationApplies) {
      this.noShowRegulationEnabled = true;
    }
  }

  dispatchActiveAction(value: string) {
    this.service.dispatchChooseActionMode(value);
  }

  sendOptionRequest(quote: QuoteRequest) {
    this.validatePassengersSelected()
      ? (this.showAvailableOptionModal = true)
      : this.service.dispatchOptionQuote(quote);
  }

  closeActionModal() {
    this.showAvailableOptionModal = false;
    this.service.dispatchCloseModalAction();
  }

  getHasNoItinerary(): Observable<boolean> {
    return of(this.pnrInfo ? (!this.pnrInfo.itinerary?.segments?.length && !this.pnrInfo.isOpen) : false);
  }

  navigateToWithParams(route: string) {
    this.service.navigateToWithParams(route);
  }

  confirmAction(actionType: string) {
    this.service.dispatchHideOrShowExceptionSwitch(false);
    switch (actionType) {
      case OptionType.Cancellation: {
        this.service.navigateTo('/cancellation/step1');
        break;
      }
      case OptionType.SpecialRequest: {
        this.service.navigateTo('/special-request');
        break;
      }
      case OptionType.VoluntaryChange: {
        this.service.navigateTo('/voluntary-change');
        break;
      }
      case OptionType.OpenTicket: {
        this.service.navigateTo('/open-ticket');
        break;
      }
      case OptionType.NameChange: {
        this.service.navigateTo('/name-change');
        break;
      }
      case OptionType.ExchangeNoCost: {
        this.service.navigateTo('/involuntary-change/no-cost-exchange');
        break;
      }
      default: {
        break;
      }
    }
  }

  validatePassengersSelected() {
    let validation = null;
    let passengersSelected = [];
    this.selectedPassengersQuote$.subscribe((passengers) => (passengersSelected = passengers));
    if (
      this.pnrInfo.passengers.length === 2 &&
      this.pnrInfo.passengers.find((pass) => pass.adultInCharge)
    ) {
      passengersSelected.length < 2 && (validation = true);
    } else {
      passengersSelected.map((passenger1) => {
        if (passenger1.adultInCharge) {
          validation = passengersSelected.find(
            (passenger2) =>
              passenger2.firstName.concat(' ' + passenger2.lastName) === passenger1.adultInCharge
          );
          validation ? (validation = null) : (validation = true);
        }
      });
    }
    return validation;
  }
}
